import { parseGuide } from "./parser";
import { createGuideSearch, type GuideSearch } from "./search";
import type { Guide } from "./types";

/**
 * Record of guide file paths to their raw content.
 * Keys are relative paths like "../../guides/getting-started.md"
 * Values are the raw markdown content including frontmatter.
 */
export type GuideFiles = Record<string, string>;

/**
 * Extracts the slug from a guide file path.
 * Handles paths like "../../guides/getting-started.md" -> "getting-started"
 *
 * @param path - The file path to extract slug from
 * @returns The slug derived from the filename
 */
export function extractSlug(path: string): string {
  // Get the filename from the path
  const filename = path.split("/").pop() ?? path;
  // Remove the .md extension
  return filename.replace(/\.md$/, "");
}

/**
 * Checks if content looks like valid markdown with frontmatter.
 */
function isValidGuideContent(content: unknown): content is string {
  return typeof content === "string" && content.startsWith("---\n");
}

/**
 * Loads and parses guide files into Guide objects.
 * Invalid files fail fast so missing guide content is visible during development and release checks.
 *
 * @param guideFiles - Record of file paths to raw markdown content
 * @returns Array of parsed Guide objects
 */
export function loadGuides(guideFiles: GuideFiles): Guide[] {
  const guides: Guide[] = [];

  for (const [path, content] of Object.entries(guideFiles)) {
    if (!isValidGuideContent(content)) {
      throw new Error(
        `Invalid guide content for ${path}: expected markdown text starting with frontmatter delimiter`
      );
    }

    const slug = extractSlug(path);
    try {
      guides.push(parseGuide(slug, content));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to parse guide ${path}: ${message}`);
    }
  }

  return guides;
}

/**
 * Creates a guide search instance from raw guide files.
 *
 * @param guideFiles - Record of file paths to raw markdown content
 * @returns GuideSearch instance with search, getGuide, and getAllGuides methods
 */
export function createGuideSearchFromFiles(guideFiles: GuideFiles): GuideSearch {
  const guides = loadGuides(guideFiles);
  return createGuideSearch(guides);
}
